import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  ArrowLeft,
  Package,
  UserCheck,
  LayoutGrid,
  List,
  Dumbbell,
  Clock,
  Calendar,
  ChevronRight,
  Hash,
  Activity,
  Zap,
  Shield,
  Target,
  FileText,
  Users,
  Trash2,
  Loader2,
  Pin,
  Search,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { supabase } from '@/lib/customSupabaseClient';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { toast } from 'sonner';

const MAX_PINNED_SESSIONS = 3;

const COACH_ASSIGNED_KEY = '__coach_assigned__';

// ---------------------------------------------------------------------------
// Skeletons
// ---------------------------------------------------------------------------

const ProgramGridSkeleton = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
    {Array.from({ length: 8 }).map((_, i) => (
      <Card key={i} className="overflow-hidden">
        <Skeleton className="h-40 w-full" />
        <CardContent className="p-4 space-y-2">
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
        </CardContent>
      </Card>
    ))}
  </div>
);

const SessionGridSkeleton = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
    {Array.from({ length: 6 }).map((_, i) => (
      <Card key={i} className="overflow-hidden">
        <Skeleton className="h-36 w-full" />
        <CardContent className="p-4 space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </CardContent>
      </Card>
    ))}
  </div>
);

const SessionDetailSkeleton = () => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Card>
          <Skeleton className="h-64 w-full rounded-t-lg" />
          <CardContent className="p-6 space-y-3">
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </CardContent>
        </Card>
      </div>
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-4 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
    <Card>
      <CardHeader><Skeleton className="h-5 w-32" /></CardHeader>
      <CardContent className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full rounded-lg" />
        ))}
      </CardContent>
    </Card>
  </div>
);

const ErrorDisplay = ({ message }) => (
  <Card className="max-w-md mx-auto">
    <CardHeader className="text-center">
      <CardTitle className="text-destructive">{message}</CardTitle>
    </CardHeader>
  </Card>
);

// ---------------------------------------------------------------------------
// Delete Confirmation Dialog
// ---------------------------------------------------------------------------

const DeleteSessionDialog = ({ session, open, onOpenChange, onConfirm, deleting }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Delete Session</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete <span className="font-medium text-foreground">{session?.name}</span>?
          This will also remove all associated exercises, client assignments, and training history. This action cannot be undone.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={deleting}>
          Cancel
        </Button>
        <Button variant="destructive" onClick={onConfirm} disabled={deleting}>
          {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Delete
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// ---------------------------------------------------------------------------
// Level 1 – Program Cards Grid
// ---------------------------------------------------------------------------

const ProgramCard = ({ program, coachSessionCount, onClick }) => {
  const isCoachAssigned = program.id === COACH_ASSIGNED_KEY;
  return (
    <Card
      className="overflow-hidden cursor-pointer transition-all hover:shadow-md hover:border-foreground/20 group"
      onClick={onClick}
    >
      <div className="relative h-40 bg-muted overflow-hidden">
        {isCoachAssigned ? (
          <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-amber-500/10 to-orange-500/10">
            <UserCheck className="h-12 w-12 text-amber-600/60" />
          </div>
        ) : program.image_url ? (
          <img
            src={program.image_url}
            alt={program.name}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center">
            <Package className="h-12 w-12 text-muted-foreground/40" />
          </div>
        )}
      </div>
      <CardContent className="p-4 space-y-2">
        <p className="text-sm font-semibold text-foreground truncate">{program.name}</p>
        <div className="flex flex-wrap gap-1.5">
          {isCoachAssigned ? (
            <Badge variant="secondary" className="text-xs">
              {coachSessionCount} session{coachSessionCount !== 1 ? 's' : ''}
            </Badge>
          ) : (
            <>
              <Badge variant="secondary" className="text-xs">
                {program.sessions_count ?? 0} sessions
              </Badge>
              <Badge variant="outline" className="text-xs">
                {program.exercises_count ?? 0} exercises
              </Badge>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const ProgramGrid = ({ programs, coachSessionCount, onSelect }) => {
  if (!programs.length && coachSessionCount === 0) {
    return <EmptyState icon={Package} title="No programs" description="No programs or sessions found." />;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {programs.map((p) => (
        <ProgramCard key={p.id} program={p} coachSessionCount={0} onClick={() => onSelect(p.id)} />
      ))}
      {coachSessionCount > 0 && (
        <ProgramCard
          program={{ id: COACH_ASSIGNED_KEY, name: 'Coach Assigned', image_url: null }}
          coachSessionCount={coachSessionCount}
          onClick={() => onSelect(COACH_ASSIGNED_KEY)}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Level 2 – Sessions List / Grid
// ---------------------------------------------------------------------------

const SessionCard = ({ session, assignedClients, onDelete, onClick }) => (
  <Card
    className="overflow-hidden cursor-pointer transition-all hover:shadow-md hover:border-foreground/20 group"
    onClick={onClick}
  >
    <div className="relative h-36 bg-muted overflow-hidden">
      {session.image_url ? (
        <img
          src={session.image_url}
          alt={session.name}
          className="h-full w-full object-cover transition-transform group-hover:scale-105"
        />
      ) : (
        <div className="h-full w-full flex items-center justify-center">
          <Calendar className="h-10 w-10 text-muted-foreground/40" />
        </div>
      )}
      {session.session_number != null && (
        <div className="absolute bottom-0 left-0 w-1/3 h-full flex items-end justify-start pointer-events-none">
          <span className="text-[3rem] font-black leading-none text-white/90 pl-2.5 pb-1.5 drop-shadow-[0_2px_6px_rgba(0,0,0,0.7)]">
            {session.session_number}
          </span>
        </div>
      )}
      {onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(session); }}
          className="absolute top-2 right-2 h-7 w-7 rounded-md bg-background/80 backdrop-blur-sm border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive hover:text-destructive-foreground hover:border-destructive"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
    <CardContent className="p-4 space-y-2">
      <p className="text-sm font-medium text-foreground truncate">{session.name}</p>
      <p className="text-xs text-muted-foreground">
        Week {session.week_number ?? '–'}, Day {session.day_of_week ?? '–'}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {session.session_type && <Badge variant="secondary" className="text-xs">{session.session_type}</Badge>}
        <Badge variant="outline" className="text-xs">{session.exercise_count ?? 0} exercises</Badge>
      </div>
      {assignedClients?.length > 0 && (
        <div className="flex items-center gap-1.5 pt-1 border-t mt-2">
          <Users className="h-3 w-3 text-muted-foreground flex-shrink-0" />
          <p className="text-xs text-muted-foreground truncate">
            {assignedClients.map((c) => c.name).join(', ')}
          </p>
        </div>
      )}
    </CardContent>
  </Card>
);

const SessionRow = ({ session, assignedClients, onDelete, onClick }) => (
  <div
    className="flex items-center gap-4 p-3 rounded-lg border cursor-pointer transition-colors hover:bg-muted/50 group"
    onClick={onClick}
  >
    <div className="relative h-12 w-12 rounded-md bg-muted overflow-hidden flex-shrink-0">
      {session.image_url ? (
        <img src={session.image_url} alt={session.name} className="h-full w-full object-cover" />
      ) : (
        <div className="h-full w-full flex items-center justify-center">
          <Calendar className="h-5 w-5 text-muted-foreground/40" />
        </div>
      )}
      {session.session_number != null && (
        <span className="absolute inset-0 flex items-center justify-center text-sm font-black text-white/90 drop-shadow-[0_1px_3px_rgba(0,0,0,0.7)]">
          {session.session_number}
        </span>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-foreground truncate">{session.name}</p>
      <p className="text-xs text-muted-foreground">
        Week {session.week_number ?? '–'}, Day {session.day_of_week ?? '–'}
      </p>
      {assignedClients?.length > 0 && (
        <div className="flex items-center gap-1.5 mt-0.5">
          <Users className="h-3 w-3 text-muted-foreground flex-shrink-0" />
          <p className="text-xs text-muted-foreground truncate">
            {assignedClients.map((c) => c.name).join(', ')}
          </p>
        </div>
      )}
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      {session.session_type && <Badge variant="secondary" className="text-xs">{session.session_type}</Badge>}
      <Badge variant="outline" className="text-xs">{session.exercise_count ?? 0} ex.</Badge>
      {onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(session); }}
          className="h-7 w-7 rounded-md border flex items-center justify-center text-muted-foreground opacity-0 group-hover:opacity-100 transition-all hover:bg-destructive hover:text-destructive-foreground hover:border-destructive"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
      <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
    </div>
  </div>
);

const SessionListView = ({ sessions, programName, assignmentsMap, isCoachAssigned, onBack, onSelectSession, onDeleteSession }) => {
  const [viewMode, setViewMode] = useState('grid');

  const sorted = useMemo(
    () =>
      [...sessions].sort(
        (a, b) => (a.week_number ?? 0) - (b.week_number ?? 0) || (a.day_of_week ?? 0) - (b.day_of_week ?? 0)
      ),
    [sessions]
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="outline" size="icon" className="flex-shrink-0 h-8 w-8" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Programs</p>
            <h2 className="text-lg font-semibold tracking-tight truncate">{programName}</h2>
          </div>
        </div>
        <div className="flex items-center gap-1 border rounded-md p-0.5">
          <Button
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            size="icon"
            className="h-7 w-7"
            onClick={() => setViewMode('grid')}
          >
            <LayoutGrid className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="icon"
            className="h-7 w-7"
            onClick={() => setViewMode('list')}
          >
            <List className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {sorted.length === 0 ? (
        <EmptyState icon={Calendar} title="No sessions" description="This program has no sessions yet." />
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {sorted.map((s) => (
            <SessionCard key={s.id} session={s} assignedClients={assignmentsMap?.[s.id]} onDelete={isCoachAssigned ? onDeleteSession : undefined} onClick={() => onSelectSession(s.id)} />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {sorted.map((s) => (
            <SessionRow key={s.id} session={s} assignedClients={assignmentsMap?.[s.id]} onDelete={isCoachAssigned ? onDeleteSession : undefined} onClick={() => onSelectSession(s.id)} />
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Level 3 – Session Template Detail
// ---------------------------------------------------------------------------

const MetadataItem = ({ icon: Icon, label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium text-foreground">{String(value)}</p>
      </div>
    </div>
  );
};

const ExerciseRow = ({ item, index }) => {
  const ex = item.exercises;
  return (
    <Link
      to={`/exercise/${item.exercise_id}`}
      className="flex items-center gap-4 p-3 rounded-lg border transition-colors hover:bg-muted/50 group"
    >
      <span className="text-xs font-mono text-muted-foreground w-5 text-center flex-shrink-0">
        {item.order_in_session ?? index + 1}
      </span>
      <div className="h-10 w-10 rounded-md bg-muted overflow-hidden flex-shrink-0">
        {ex?.video_thumbnail_url ? (
          <img src={ex.video_thumbnail_url} alt={ex.name} className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full flex items-center justify-center">
            <Dumbbell className="h-4 w-4 text-muted-foreground/40" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{ex?.name ?? 'Unknown exercise'}</p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
          {item.sets != null && <span>{item.sets} sets</span>}
          {item.reps != null && <span>{item.reps} reps</span>}
          {item.weight_kg != null && <span>{item.weight_kg} kg</span>}
          {item.duration_seconds != null && <span>{item.duration_seconds}s</span>}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {item.exercise_group && (
          <Badge variant="outline" className="text-xs capitalize">{item.exercise_group}</Badge>
        )}
        {item.is_optional && <Badge variant="secondary" className="text-xs">Optional</Badge>}
        <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
    </Link>
  );
};

const SessionTemplateDetail = ({ session, programName, assignedClients, isCoachAssigned, onDeleteSession, onBackToProgram, onBackToPrograms }) => {
  const [exercises, setExercises] = useState([]);
  const [loadingExercises, setLoadingExercises] = useState(true);

  // Pin-for-athlete state
  const [pinForAthleteOpen, setPinForAthleteOpen] = useState(false);
  const [athleteSearchTerm, setAthleteSearchTerm] = useState('');
  const [athleteResults, setAthleteResults] = useState([]);
  const [athleteSearchLoading, setAthleteSearchLoading] = useState(false);
  const [athletePinCounts, setAthletePinCounts] = useState({});
  const [pinningForUserId, setPinningForUserId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetchExercises = async () => {
      setLoadingExercises(true);
      try {
        const { data, error } = await supabase
          .from('session_exercises')
          .select('*, exercises(id, name, video_thumbnail_url, difficulty)')
          .eq('session_id', session.id)
          .order('order_in_session');

        if (!cancelled && !error) setExercises(data || []);
      } catch {
        // non-critical
      } finally {
        if (!cancelled) setLoadingExercises(false);
      }
    };
    fetchExercises();
    return () => { cancelled = true; };
  }, [session.id]);

  // Debounced athlete search for pin-for-athlete dialog
  useEffect(() => {
    if (!pinForAthleteOpen || !athleteSearchTerm.trim()) {
      setAthleteResults([]);
      setAthletePinCounts({});
      return;
    }
    const timer = setTimeout(async () => {
      setAthleteSearchLoading(true);
      try {
        const { data, error } = await supabase.rpc('search_users_with_activity', {
          p_search_term: athleteSearchTerm.trim(),
          p_limit: 15,
        });
        if (error) throw error;
        const athletes = data || [];
        setAthleteResults(athletes);

        // Fetch pin counts for returned athletes
        const ids = athletes.map(a => a.id).filter(Boolean);
        if (ids.length > 0) {
          const { data: pins } = await supabase
            .from('user_pinned_sessions')
            .select('user_id')
            .in('user_id', ids);
          const counts = {};
          for (const p of (pins || [])) {
            counts[p.user_id] = (counts[p.user_id] || 0) + 1;
          }
          setAthletePinCounts(counts);
        }
      } catch (err) {
        console.error('Error searching athletes:', err);
      } finally {
        setAthleteSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [athleteSearchTerm, pinForAthleteOpen]);

  const handlePinForAthlete = async (athleteId, athleteName) => {
    setPinningForUserId(athleteId);
    try {
      const { data: existing } = await supabase
        .from('user_pinned_sessions')
        .select('sort_order')
        .eq('user_id', athleteId)
        .order('sort_order', { ascending: false })
        .limit(1);

      const nextOrder = existing?.length ? existing[0].sort_order + 1 : 0;

      const { error } = await supabase
        .from('user_pinned_sessions')
        .insert({ user_id: athleteId, session_id: session.id, sort_order: nextOrder });

      if (error) {
        if (error.message?.includes('PIN_LIMIT_REACHED')) {
          toast.error(`${athleteName} already has 3 pinned sessions. Remove one first from their profile.`);
          return;
        }
        if (error.code === '23505') {
          toast.info(`"${session.name}" is already pinned for ${athleteName}.`);
          setPinForAthleteOpen(false);
          return;
        }
        throw error;
      }

      toast.success(`Pinned "${session.name}" for ${athleteName}`);
      setPinForAthleteOpen(false);
      setAthleteSearchTerm('');
    } catch (err) {
      console.error('Error pinning for athlete:', err);
      toast.error('Could not pin session', { description: err.message });
    } finally {
      setPinningForUserId(null);
    }
  };

  const equipment = Array.isArray(session.equipment_required) && session.equipment_required.length > 0
    ? session.equipment_required.join(', ')
    : null;

  return (
    <div className="space-y-6">
      {/* Breadcrumb nav */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="outline" size="icon" className="flex-shrink-0 h-8 w-8" onClick={onBackToProgram}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0">
            <button onClick={onBackToPrograms} className="hover:text-foreground transition-colors">Programs</button>
            <ChevronRight className="h-3 w-3 flex-shrink-0" />
            <button onClick={onBackToProgram} className="hover:text-foreground transition-colors truncate">{programName}</button>
            <ChevronRight className="h-3 w-3 flex-shrink-0" />
            <span className="text-foreground font-medium truncate">{session.name}</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button variant="outline" size="sm" onClick={() => { setPinForAthleteOpen(true); setAthleteSearchTerm(''); }}>
            <Pin className="h-3.5 w-3.5 mr-1.5" />
            Pin for Athlete
          </Button>
          {isCoachAssigned && onDeleteSession && (
            <Button variant="outline" size="sm" className="text-destructive hover:bg-destructive hover:text-destructive-foreground" onClick={() => onDeleteSession(session)}>
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              Delete Session
            </Button>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="overflow-hidden">
            {session.image_url ? (
              <img
                src={session.image_url}
                alt={session.name}
                className="w-full h-auto max-h-72 object-cover"
              />
            ) : (
              <div className="w-full h-48 bg-muted flex items-center justify-center">
                <Calendar className="h-12 w-12 text-muted-foreground/30" />
              </div>
            )}
            <CardContent className="p-6 space-y-3">
              <div>
                <h2 className="text-lg font-semibold">{session.name}</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {session.description || 'No description provided.'}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {session.session_type && <Badge variant="secondary">{session.session_type}</Badge>}
                {session.session_category && <Badge variant="outline">{session.session_category}</Badge>}
                {session.rehabilitation_phase && <Badge variant="outline">{session.rehabilitation_phase}</Badge>}
              </div>
            </CardContent>
          </Card>

          {(session.clinical_reasoning || session.primary_goal) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Clinical Context</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {session.primary_goal && (
                  <MetadataItem icon={Target} label="Primary Goal" value={session.primary_goal} />
                )}
                {session.secondary_goals?.length > 0 && (
                  <MetadataItem icon={Target} label="Secondary Goals" value={session.secondary_goals.join(', ')} />
                )}
                {session.clinical_reasoning && (
                  <MetadataItem icon={FileText} label="Clinical Reasoning" value={session.clinical_reasoning} />
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Session Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <MetadataItem icon={Calendar} label="Schedule" value={`Week ${session.week_number ?? '–'}, Day ${session.day_of_week ?? '–'}`} />
              <MetadataItem icon={Clock} label="Duration" value={session.duration_minutes ? `${session.duration_minutes} min` : null} />
              <MetadataItem icon={Activity} label="Intensity Level" value={session.intensity_level} />
              <MetadataItem icon={Zap} label="Metabolic Demand" value={session.metabolic_demand} />
              <MetadataItem icon={Hash} label="Rest Between Sets" value={session.rest_periods_seconds ? `${session.rest_periods_seconds}s` : null} />
              <MetadataItem icon={Shield} label="Supervision Level" value={session.supervision_level} />
              {equipment && <MetadataItem icon={Package} label="Equipment" value={equipment} />}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Stats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <MetadataItem icon={Hash} label="Exercise Count" value={session.exercise_count ?? exercises.length} />
              <MetadataItem icon={Activity} label="Volume Load" value={session.volume_load} />
              <MetadataItem icon={Clock} label="Recovery Time" value={session.recovery_time_hours ? `${session.recovery_time_hours}h` : null} />
            </CardContent>
          </Card>
          {assignedClients?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  Assigned Clients ({assignedClients.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {assignedClients.map((client) => (
                    <Link
                      key={client.id}
                      to={`/user-activity/${client.id}`}
                      className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 transition-colors"
                    >
                      <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                        <UserCheck className="h-3.5 w-3.5 text-muted-foreground" />
                      </div>
                      <span className="text-sm font-medium text-foreground truncate">{client.name}</span>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Exercises */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Dumbbell className="h-4 w-4 text-muted-foreground" />
            Exercises ({loadingExercises ? '...' : exercises.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingExercises ? (
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          ) : exercises.length === 0 ? (
            <EmptyState icon={Dumbbell} title="No exercises" description="No exercises are prescribed for this session." />
          ) : (
            <div className="space-y-2">
              {exercises.map((item, idx) => (
                <ExerciseRow key={item.id} item={item} index={idx} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pin for Athlete Dialog */}
      <Dialog open={pinForAthleteOpen} onOpenChange={(open) => { setPinForAthleteOpen(open); if (!open) { setAthleteSearchTerm(''); setAthleteResults([]); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Pin "{session.name}" for an Athlete</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search athletes by name or email…"
                value={athleteSearchTerm}
                onChange={(e) => setAthleteSearchTerm(e.target.value)}
                className="pl-9"
                autoFocus
              />
            </div>
            <div className="max-h-72 overflow-y-auto space-y-1">
              {athleteSearchLoading ? (
                <div className="space-y-2 p-2">
                  {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 w-full rounded-md" />)}
                </div>
              ) : athleteSearchTerm.trim() && athleteResults.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No athletes found.</p>
              ) : athleteResults.length > 0 ? (
                athleteResults.map((athlete) => {
                  const pinCount = athletePinCounts[athlete.id] || 0;
                  const isFull = pinCount >= MAX_PINNED_SESSIONS;
                  return (
                    <button
                      key={athlete.id}
                      className="w-full flex items-center justify-between rounded-md p-2.5 text-left hover:bg-muted/50 transition-colors disabled:opacity-50"
                      disabled={isFull || pinningForUserId === athlete.id}
                      title={isFull ? `${athlete.full_name || 'This athlete'} already has ${MAX_PINNED_SESSIONS} pins` : undefined}
                      onClick={() => handlePinForAthlete(athlete.id, athlete.full_name || athlete.email)}
                    >
                      <div className="min-w-0 flex-1">
                        <span className="text-sm font-medium truncate block">{athlete.full_name || 'Unnamed'}</span>
                        <span className="text-xs text-muted-foreground">{athlete.email}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <Badge variant={isFull ? 'destructive' : 'secondary'} className="text-xs">
                          {pinCount}/{MAX_PINNED_SESSIONS}
                        </Badge>
                        {pinningForUserId === athlete.id ? (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        ) : (
                          <Pin className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    </button>
                  );
                })
              ) : (
                <p className="text-sm text-muted-foreground text-center py-6">Search for an athlete to pin this session to their dashboard.</p>
              )}
            </div>
            <p className="text-xs text-muted-foreground">Changes appear on the athlete's dashboard within 2 minutes.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Sessions Component
// ---------------------------------------------------------------------------

const Sessions = () => {
  const { can } = useAuth();
  const canDeleteSessions = can('sessions.delete');
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedProgramId = searchParams.get('program');
  const selectedSessionId = searchParams.get('session');

  const [programs, setPrograms] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [assignmentsMap, setAssignmentsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [
          { data: programsData, error: programsError },
          { data: sessionsData, error: sessionsError },
          { data: assignmentsData },
          { data: thumbData },
        ] = await Promise.all([
          supabase.from('admin_programs_v').select('*'),
          supabase.from('admin_sessions_v').select('*'),
          supabase
            .from('client_assigned_sessions')
            .select('session_id, client_id, profiles:client_id(full_name)')
            .eq('is_active', true),
          supabase
            .from('session_exercises')
            .select('session_id, order_in_session, exercises(video_thumbnail_url)')
            .order('order_in_session'),
        ]);

        if (programsError) throw programsError;
        if (sessionsError) throw sessionsError;

        const thumbMap = {};
        (thumbData || []).forEach((se) => {
          if (!thumbMap[se.session_id] && se.exercises?.video_thumbnail_url) {
            thumbMap[se.session_id] = se.exercises.video_thumbnail_url;
          }
        });

        setPrograms(programsData || []);
        setSessions(
          (sessionsData || []).map((s) => ({
            ...s,
            image_url: s.image_url || thumbMap[s.id] || null,
          }))
        );

        const aMap = {};
        (assignmentsData || []).forEach((a) => {
          if (!aMap[a.session_id]) aMap[a.session_id] = [];
          const name = a.profiles?.full_name || 'Unknown';
          if (!aMap[a.session_id].some((c) => c.id === a.client_id)) {
            aMap[a.session_id].push({ id: a.client_id, name });
          }
        });
        setAssignmentsMap(aMap);
      } catch (err) {
        setError('Failed to fetch data.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Multi-block child blocks execute inside their parent session — never list them as standalone sessions
  const coachSessions = useMemo(() => sessions.filter((s) => !s.program_id && !s.parent_session_id), [sessions]);

  const handleDeleteSession = useCallback(async () => {
    if (!sessionToDelete) return;
    setDeleting(true);
    try {
      const { error: deleteError } = await supabase
        .from('sessions')
        .delete()
        .eq('id', sessionToDelete.id);

      if (deleteError) throw deleteError;

      setSessions((prev) => prev.filter((s) => s.id !== sessionToDelete.id));
      setAssignmentsMap((prev) => {
        const next = { ...prev };
        delete next[sessionToDelete.id];
        return next;
      });

      if (selectedSessionId === sessionToDelete.id) {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete('session');
          return next;
        });
      }

      toast.success('Session deleted', { description: `"${sessionToDelete.name}" has been permanently deleted.` });
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete session', { description: err.message || 'An unexpected error occurred.' });
    } finally {
      setDeleting(false);
      setSessionToDelete(null);
    }
  }, [sessionToDelete, selectedSessionId, setSearchParams]);

  const navigateToProgram = useCallback(
    (programId) => {
      setSearchParams({ program: programId });
    },
    [setSearchParams]
  );

  const navigateToSession = useCallback(
    (sessionId) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('session', sessionId);
        return next;
      });
    },
    [setSearchParams]
  );

  const navigateBack = useCallback(() => {
    if (selectedSessionId) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('session');
        return next;
      });
    } else {
      setSearchParams({});
    }
  }, [selectedSessionId, setSearchParams]);

  const navigateToPrograms = useCallback(() => setSearchParams({}), [setSearchParams]);

  // Derive current view data
  const currentProgram = useMemo(() => {
    if (!selectedProgramId) return null;
    if (selectedProgramId === COACH_ASSIGNED_KEY) return { id: COACH_ASSIGNED_KEY, name: 'Coach Assigned' };
    return programs.find((p) => p.id === selectedProgramId) || null;
  }, [selectedProgramId, programs]);

  const filteredSessions = useMemo(() => {
    if (!selectedProgramId) return [];
    if (selectedProgramId === COACH_ASSIGNED_KEY) return coachSessions;
    return sessions.filter((s) => s.program_id === selectedProgramId && !s.parent_session_id);
  }, [selectedProgramId, sessions, coachSessions]);

  const currentSession = useMemo(() => {
    if (!selectedSessionId) return null;
    return sessions.find((s) => s.id === selectedSessionId) || null;
  }, [selectedSessionId, sessions]);

  // Determine page title
  let pageTitle = 'Sessions';
  if (currentSession) pageTitle = `${currentSession.name} - Sessions`;
  else if (currentProgram) pageTitle = `${currentProgram.name} - Sessions`;

  return (
    <>
      <Helmet>
        <title>{pageTitle} - My Movement Space</title>
      </Helmet>

      {/* Page header only on Level 1 */}
      {!selectedProgramId && (
        <div className="space-y-2 mb-8">
          <h1 className="text-2xl font-semibold tracking-tight">Sessions</h1>
          <p className="text-sm text-muted-foreground">Browse programs and their training sessions.</p>
        </div>
      )}

      {loading ? (
        selectedSessionId ? (
          <SessionDetailSkeleton />
        ) : selectedProgramId ? (
          <SessionGridSkeleton />
        ) : (
          <ProgramGridSkeleton />
        )
      ) : error ? (
        <ErrorDisplay message={error} />
      ) : selectedSessionId && currentSession ? (
        <SessionTemplateDetail
          session={currentSession}
          programName={currentProgram?.name ?? 'Unknown'}
          assignedClients={assignmentsMap[currentSession.id]}
          isCoachAssigned={selectedProgramId === COACH_ASSIGNED_KEY}
          onDeleteSession={canDeleteSessions ? setSessionToDelete : undefined}
          onBackToProgram={navigateBack}
          onBackToPrograms={navigateToPrograms}
        />
      ) : selectedProgramId && currentProgram ? (
        <SessionListView
          sessions={filteredSessions}
          programName={currentProgram.name}
          assignmentsMap={assignmentsMap}
          isCoachAssigned={selectedProgramId === COACH_ASSIGNED_KEY}
          onBack={navigateBack}
          onSelectSession={navigateToSession}
          onDeleteSession={canDeleteSessions ? setSessionToDelete : undefined}
        />
      ) : (
        <ProgramGrid
          programs={programs}
          coachSessionCount={coachSessions.length}
          onSelect={navigateToProgram}
        />
      )}

      <DeleteSessionDialog
        session={sessionToDelete}
        open={!!sessionToDelete}
        onOpenChange={(open) => { if (!open) setSessionToDelete(null); }}
        onConfirm={handleDeleteSession}
        deleting={deleting}
      />
    </>
  );
};

export default Sessions;
